import { useQuery } from '@tanstack/react-query';
import { auditApi, overviewApi } from '@/lib/api';
import { useAuthStore } from '@/hooks/useAuthStore';

export default function Dashboard() {
  const { staff } = useAuthStore();
  const isAdmin = staff?.role === 'ADMIN' || staff?.role === 'SUPER_ADMIN';

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['overviewStats'],
    queryFn: overviewApi.getStats,
  });

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['auditLogs'],
    queryFn: () => auditApi.getLogs({ limit: 10 }),
  });

  const { data: securityData } = useQuery({
    queryKey: ['securityAlerts'],
    queryFn: overviewApi.getSecurity,
    enabled: isAdmin,
  });

  const stats = statsData?.data?.stats;
  const security = securityData?.data?.security;

  return (
    <>
      {/* Welcome Banner */}
      <div className="card mb-6 bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Bienvenido, {staff?.nombre}</h1>
            <p className="text-primary-100">Panel de Administracion de SolidCycle</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-primary-100">Ultimo acceso</p>
            <p className="font-medium">{new Date().toLocaleDateString('es-MX')}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-primary-100 rounded-lg p-3">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Miembros</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '--' : stats?.miembros?.total || 0}
              </p>
              <p className="text-xs text-gray-500">
                {stats?.miembros?.activos || 0} activos
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Hogares</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '--' : stats?.hogares || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Movimientos</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '--' : stats?.movimientos || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-accent-500/10 rounded-lg p-3">
              <svg className="w-6 h-6 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Audit Logs</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '--' : stats?.auditLogs || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Security and Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Security Panel (Admin only) */}
        {isAdmin && security && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Seguridad (24h)</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Score de Seguridad</span>
                <span className={`text-2xl font-bold ${
                  security.securityScore >= 80 ? 'text-green-600' :
                  security.securityScore >= 50 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {security.securityScore}%
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Logins fallidos</span>
                  <span className={security.last24h.failedLogins > 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                    {security.last24h.failedLogins}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">MFA fallidos</span>
                  <span className={security.last24h.mfaFailures > 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                    {security.last24h.mfaFailures}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Acciones criticas</span>
                  <span className={security.last24h.criticalActions > 0 ? 'text-orange-600 font-medium' : 'text-gray-900'}>
                    {security.last24h.criticalActions}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Cuentas bloqueadas</span>
                  <span className={security.lockedAccounts > 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                    {security.lockedAccounts}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity Table */}
        <div className={`card ${isAdmin && security ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Actividad Reciente</h2>
          </div>

          {auditLoading ? (
            <div className="text-center py-8 text-gray-500">Cargando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accion
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditData?.data?.logs?.map((log: {
                    id: string;
                    timestamp: string;
                    action: string;
                    staffEmail: string;
                    ipAddress: string;
                    severity?: string;
                  }) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(log.timestamp).toLocaleString('es-MX')}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          log.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                          log.severity === 'WARNING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">{log.staffEmail}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">{log.ipAddress}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Staff Stats */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Administrativo</h2>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="text-3xl font-bold text-purple-600">{stats?.staff || 0}</div>
            <div className="text-sm text-gray-500">Staff Activo</div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">{stats?.categorias || 0}</div>
            <div className="text-sm text-gray-500">Categorias</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600">{stats?.presupuestos || 0}</div>
            <div className="text-sm text-gray-500">Presupuestos</div>
          </div>
        </div>
      </div>
    </>
  );
}
